import React, {useEffect, useRef, useState} from 'react';
import {Chart as ChartJS, ArcElement, Tooltip, Legend, ChartOptions} from 'chart.js';
import {Doughnut} from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {cn} from '../ui/lib/utils';
import {RuntimeVariable} from '../Dashboarding/Dashboard';
import {substituteVariablesInText, substituteVariablesInFilters} from './utils';
import {GetAggregateMultiMetricEvaluation, createMultiMetricRequest} from "../../clients/metoro/metrics";
import {MetricSpecifier} from "./MultiMetoroMetricsCharts";
import {WindowUnit} from "../../pages/alerts/MetricAlert";
import {useSelector} from "react-redux";
import timerange from "../../store/reducers/timerange";
import {LoadingSpinner} from "../ui/customSpinner";
import {StatValue, getAggregateStartEndTime} from "./StatChart";
import {Formula} from "../../types/formula";
import {toast} from "../ui/use-toast";
import { formatValueWithUnit } from "../../utility/unitConverter";

// Define a custom type for the doughnut chart configuration
interface DoughnutChartConfig {
    type: 'doughnut';
    data: {
        datasets: {
            data: number[];
            backgroundColor: (ctx: any) => string | undefined;
        }[];
    };
    options: ChartOptions<'doughnut'> & {
        plugins: {
            annotation: {
                annotations: {
                    [key: string]: any;
                };
            };
        };
    };
}

// Register required Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, annotationPlugin, ChartDataLabels);

// Define color thresholds for the gauge
const COLORS = ['#04d966', 'rgb(239, 198, 0)', 'rgb(231, 24, 49)'];

// Helper function to determine color index based on percentage
function index(perc: number) {
    return perc < 70 ? 0 : perc < 90 ? 1 : 2;
}

interface GaugeChartProps {
    className?: string;
    min: number;
    max: number;
    precision: number;
    showAbsoluteValue?: boolean;
    unitOverride?: string;
    runtimeVariables?: RuntimeVariable[];
    metricSpecifiers: MetricSpecifier[];
    formulas?: Formula[];
    metoroQLQueries?: any[];
    reduceOption: string;
    environments: string[];
}

export function GaugeChart(props: GaugeChartProps) {
    const [gaugeValues, setGaugeValues] = useState<StatValue[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(!!props.metricSpecifiers?.length);
    const [updateMetricsAbortController, setUpdateMetricsAbortController] = useState<AbortController>(new AbortController());
    const timeRange = useSelector(timerange.selectors.getTimeRange);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerDimensions, setContainerDimensions] = useState<{ width: number, height: number }>({ width: 0, height: 0 });
    const [chartConfigs, setChartConfigs] = useState<DoughnutChartConfig[]>([]);
    const [isLimitedGauges, setIsLimitedGauges] = useState<boolean>(false);

    useEffect(() => {
        gaugeValues.sort((a: StatValue, b: StatValue) => {
            // Reverse order as we push it to an array.
            if (!a.value && !b.value) return 0;
            if (!a.value) return 1;
            if (!b.value) return 1;
            if (a.value < b.value) return 1;
            if (a.value > b.value) return -1;
            return 0;
        });

        if (gaugeValues.length > 24) {
            gaugeValues.length = 24; // Limit to 64 values
            setIsLimitedGauges(true);
        } else {
            setIsLimitedGauges(false);
        }

        const configsToSet: DoughnutChartConfig[] = []
        {gaugeValues.forEach((gaugeValue, index) => {
            // Get the first attribute key if available
            const attributeKey = gaugeValue.attributes ? Object.keys(gaugeValue.attributes)[0] : undefined;
            const attributeValue = attributeKey ? gaugeValue.attributes[attributeKey] : "";
            configsToSet.push(createGaugeConfig(gaugeValue.value ?? 0, attributeValue, gaugeValue.unit || "", containerDimensions))
        })}
        setChartConfigs(configsToSet)

    }, [containerDimensions, gaugeValues, props.precision, props.min, props.max, props.showAbsoluteValue, props.unitOverride]);

    // Used to make sure that the fitting of the text happens after the component is mounted
    useEffect(() => {
        // dispatch it once mounted
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 200);
    }, [gaugeValues]); // Add gaugeValues as dependency to trigger resize when data changes

    // Set up ResizeObserver to track container dimensions
    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver(entries => {
            const entry = entries[0];
            if (entry) {
                const { width, height } = entry.contentRect;
                setContainerDimensions({ width, height });

                // Trigger chart resize and redraw
                setTimeout(() => {
                    window.dispatchEvent(new Event('resize'));
                }, 50);
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // Fetch metric data if metricSpecifiers are provided
    useEffect(() => {
        // Only fetch data if we have any data sources (metricSpecifiers, formulas, or metoroQLQueries)
        const hasMetricSpecifiers = props.metricSpecifiers && props.metricSpecifiers.length > 0;
        const hasFormulas = props.formulas && props.formulas.length > 0;
        const hasMetoroQLQueries = props.metoroQLQueries && props.metoroQLQueries.length > 0;

        if (!hasMetricSpecifiers && !hasFormulas && !hasMetoroQLQueries) {
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            try {
                let startTime = Math.floor(timeRange.getStartEnd()[0].getTime() / 1000);
                let endTime = Math.floor(timeRange.getStartEnd()[1].getTime() / 1000);

                // Align start and end times with bucket sizes - only if we have metric specifiers
                if (hasMetricSpecifiers) {
                    let [alignedStartTime, alignedEndTime] = getAggregateStartEndTime(props.metricSpecifiers, startTime, endTime);
                    startTime = alignedStartTime;
                    endTime = alignedEndTime;
                }

                // Calculate window parameters based on time range
                const calculateWindowParams = () => {
                    const timeRangeInSeconds = endTime - startTime;
                    const timeRangeInMinutes = timeRangeInSeconds / 60;
                    const timeRangeInHours = timeRangeInMinutes / 60;
                    const timeRangeInDays = timeRangeInHours / 24;

                    if (timeRangeInDays > 7) {
                        // For ranges > 7 days, use days
                        return {
                            windowSize: Math.max(1, Math.floor(timeRangeInDays / 60)),
                            windowUnit: WindowUnit.Days
                        };
                    } else if (timeRangeInHours > 24) {
                        // For ranges > 24 hours, use hours
                        return {
                            windowSize: Math.max(1, Math.floor(timeRangeInHours / 60)),
                            windowUnit: WindowUnit.Hours
                        };
                    } else {
                        // For ranges <= 24 hours, use minutes
                        return {
                            windowSize: Math.max(1, Math.floor(timeRangeInMinutes / 60)),
                            windowUnit: WindowUnit.Minutes
                        };
                    }
                };

                const {windowSize, windowUnit} = calculateWindowParams();

                // Process metric specifiers with variable substitution and environment filtering
                const processedMetricSpecifiers = hasMetricSpecifiers ? props.metricSpecifiers.map(spec => {
                    const processedFilters = substituteVariablesInFilters(spec.filters || new Map(), props.runtimeVariables);
                    const processedExcludeFilters = substituteVariablesInFilters(spec.excludeFilters || new Map(), props.runtimeVariables);

                    if (props.environments && props.environments.length > 0 && !processedFilters.has("environment")) {
                        processedFilters.set("environment", props.environments);
                    }

                    return {
                        ...spec,
                        filters: processedFilters,
                        excludeFilters: processedExcludeFilters,
                    };
                }) : [];

                const multiMetricRequest = createMultiMetricRequest(
                    processedMetricSpecifiers,
                    startTime,
                    endTime,
                    true,
                    props.formulas,
                    props.runtimeVariables,
                    props.metoroQLQueries,
                );

                const aggregateParams = {
                    evaluationFunction: props.reduceOption,
                    window: windowSize,
                    windowUnit: windowUnit,
                    evaluationSplits: hasMetricSpecifiers ? (processedMetricSpecifiers[0]?.splits || []) : [],
                };

                const response = await GetAggregateMultiMetricEvaluation(
                    multiMetricRequest,
                    aggregateParams,
                    updateMetricsAbortController,
                    setUpdateMetricsAbortController
                );

                if (response.metric.timeSeries && response.metric.timeSeries.length > 0) {
                    const values: StatValue[] = response.metric.timeSeries.map(series => {
                        return {
                            attributes: typeof series.attributes === 'object' ?
                                (series.attributes instanceof Map ?
                                    Object.fromEntries(series.attributes) :
                                    series.attributes) :
                                {},
                            value: series.data && series.data.length > 0 ? series.data[0].value : 0,
                            unit: response.metric.unit || ""
                        };
                    });
                    setGaugeValues(values);
                } else {
                    setGaugeValues([]);
                }
            } catch (error) {
                console.error("Error fetching metric data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [
        props.metricSpecifiers,
        props.formulas,
        props.metoroQLQueries,
        props.reduceOption,
        timeRange,
        props.runtimeVariables,
        props.environments
    ]);

    // Apply variable substitution to label if variables are provided
    // Function to create chart configuration for a single gauge value
    const createGaugeConfig = (gaugeValue: number, attribute?: string, unit?: string, containerDims = containerDimensions): DoughnutChartConfig => {
        // Ensure value is within bounds
        const boundedValue = Math.max(props.min, Math.min(props.max, gaugeValue));

        // Calculate percentage for display
        const percentage = ((boundedValue - props.min) / (props.max - props.min)) * 100;

        // Create chart data
        const data = {
            datasets: [{
                data: [percentage, 100 - percentage],
                borderColor: '#3793FF',
                borderWidth: 0,
                backgroundColor(ctx: any) {
                    if (ctx.type !== 'data') {
                        return;
                    }
                    if (ctx.index === 1) {
                        return '#3793ff1a';
                    }
                    return COLORS[index(ctx.raw)];
                }
            }]
        };

        // Create annotation for displaying the value (kept for backward compatibility)
        const annotation = {
            type: 'label',
            content: attribute ? attribute : "",
            display: false
        };

        // Chart configuration
        return {
            type: 'doughnut',
            data,
            options: {
                responsive: true,
                maintainAspectRatio: false,
                aspectRatio: 2,
                circumference: 180,
                rotation: -90,
                layout: {
                    padding: {
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    datalabels: {
                        display: false
                    },
                    annotation: {
                        // @ts-ignore
                        annotations: {
                            annotation
                        }
                    }
                },
                elements: {
                    arc: {
                        borderWidth: 0
                    }
                },
                scales: {},
                interaction: {
                    intersect: false
                }
            },
            key: `gauge-${gaugeValue}-${attribute}-${props.precision}-${props.max}-${props.min}-${props.showAbsoluteValue}-${props.unitOverride}-${containerDims.width}x${containerDims.height}`, // Add dimensions to force re-render on resize
            plugins: [{
                id: 'centerText',
                afterDraw: (chart: any) => {
                    const {width, height, ctx} = chart;
                    ctx.save();

                    // Calculate responsive font sizes based on chart dimensions
                    const minDimension = Math.min(width, height);
                    const maxTextWidth = minDimension * 0.75; // Increased text area

                    // Helper function to truncate text if it's too long
                    const truncateText = (text: string, maxWidth: number, fontSize: number): string => {
                        ctx.font = `normal ${fontSize}px sans-serif`;
                        if (ctx.measureText(text).width <= maxWidth) {
                            return text;
                        }

                        // Try to fit with ellipsis
                        let truncated = text;
                        while (truncated.length > 0 && ctx.measureText(truncated + '...').width > maxWidth) {
                            truncated = truncated.slice(0, -1);
                        }
                        return truncated.length > 0 ? truncated + '...' : '';
                    };

                    // More dynamic font size calculation with better scaling
                    let percentageFontSize, labelFontSize;

                    // Calculate the actual gauge chart height (semicircle)
                    // For a semicircle gauge, the visible height is approximately the radius
                    const gaugeRadius = Math.min(width, height) / 2;
                    const gaugeHeight = gaugeRadius; // Height of the semicircle

                    // Use gauge height-based sizing as requested
                    // Percentage text should be 1/4 of the gauge chart height
                    percentageFontSize = Math.floor(gaugeHeight * 0.35);
                    // Label text should be 1/10th of the gauge chart height  
                    labelFontSize = Math.floor(gaugeHeight * 0.15);

                    // Set minimum font sizes to ensure readability
                    percentageFontSize = Math.max(8, percentageFontSize);
                    labelFontSize = Math.max(6, labelFontSize);

                    // Calculate center position - center in the doughnut hole
                    const centerX = width / 2;
                    // For a half-circle gauge, we need to position text in the actual center of the visible area
                    // Since circumference is 180 degrees and rotation is -90, the visible area is the bottom half
                    const centerY = height - height * 0.1 // Adjusted to center vertically in the bottom half;

                    // Prepare text content - either percentage or absolute value
                    let displayText: string;
                    if (props.showAbsoluteValue) {
                        // Use unitOverride if provided, otherwise use the unit from the metric
                        const unitToUse = props.unitOverride || unit || '';
                        // Use the same formatting as StatChart for consistency
                        displayText = formatValueWithUnit(gaugeValue, unitToUse, props.precision);
                    } else {
                        // Calculate and display percentage
                        const actualPercentage = ((boundedValue - props.min) / (props.max - props.min)) * 100;
                        displayText = `${actualPercentage.toFixed(props.precision)}%`;
                    }

                    // Handle label text - prioritize attribute name if it exists
                    let primaryText = '';

                    if (attribute) {
                        // If we have an attribute, show it as primary text and label as secondary
                        primaryText = attribute;
                    }

                    // Truncate texts to fit available space
                    const truncatedPrimaryText = truncateText(primaryText, maxTextWidth, labelFontSize);

                    // Calculate text positioning for centered layout
                    const hasSecondaryText = truncatedPrimaryText.length > 0;
                    const textSpacing = percentageFontSize * 0.005

                    if (hasSecondaryText) {
                        // If we have both percentage and secondary text, center them as a group
                        const totalTextHeight = percentageFontSize + labelFontSize + textSpacing;
                        const startY = centerY - (totalTextHeight / 2);

                        // Draw percentage text
                        ctx.font = `bold ${percentageFontSize}px sans-serif`;
                        ctx.fillStyle = 'white';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'center';
                        ctx.fillText(displayText, centerX, startY);

                        // Draw secondary text
                        ctx.font = `normal ${labelFontSize}px sans-serif`;
                        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'bottom';
                        ctx.fillText(truncatedPrimaryText, centerX, startY + percentageFontSize + textSpacing);
                    } else {
                        // If only percentage text, center it alone
                        ctx.font = `bold ${percentageFontSize}px sans-serif`;
                        ctx.fillStyle = 'white';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'bottom';
                        ctx.fillText(displayText, centerX, centerY);
                    }

                    ctx.restore();
                }
            }]
        };
    };

    if (isLoading) {
        return (
            <div className={cn("w-full h-full flex items-center justify-center", props.className)} ref={containerRef}>
                n/a
            </div>
        );
    }

    // Calculate grid layout based on container dimensions and number of gauge values
    const calculateGridLayout = () => {
        const numGauges = gaugeValues.length;

        // Default to responsive grid if dimensions are not available
        if (containerDimensions.width === 0 || containerDimensions.height === 0) {
            return {
                gridTemplateColumns: numGauges === 1 ? "1fr" :
                    numGauges === 2 ? "repeat(2, 1fr)" :
                    numGauges <= 4 ? "repeat(2, 1fr)" :
                    numGauges <= 6 ? "repeat(3, 1fr)" :
                    "repeat(4, 1fr)",
                gridTemplateRows: "auto"
            };
        }

        const { width, height } = containerDimensions;

        // For a single gauge, use full container
        if (numGauges === 1) {
            return {
                gridTemplateColumns: "1fr",
                gridTemplateRows: "1fr"
            };
        }

        // Calculate optimal grid layout based on container aspect ratio and number of gauges
        let bestColumns = 1;
        let bestRows = numGauges;
        let bestScore = Infinity;

        // Try different column/row combinations
        for (let cols = 1; cols <= Math.min(numGauges, 6); cols++) {
            const rows = Math.ceil(numGauges / cols);

            // Calculate the aspect ratio each gauge would have
            const cellWidth = width / cols;
            const cellHeight = height / rows;

            // Gauges work best with a roughly 2:1 aspect ratio (wider than tall)
            // Score based on how close we get to optimal aspect ratio
            const cellAspectRatio = cellWidth / cellHeight;
            const optimalAspectRatio = 2.0;
            const aspectScore = Math.abs(cellAspectRatio - optimalAspectRatio);

            // Penalize having too many empty cells
            const emptyCells = (cols * rows) - numGauges;
            const emptyPenalty = emptyCells * 0.5;

            // Penalize very small cells
            const minCellSize = Math.min(cellWidth, cellHeight);
            const sizePenalty = minCellSize < 100 ? (100 - minCellSize) * 0.1 : 0;

            const totalScore = aspectScore + emptyPenalty + sizePenalty;

            if (totalScore < bestScore) {
                bestScore = totalScore;
                bestColumns = cols;
                bestRows = rows;
            }
        }

        return {
            gridTemplateColumns: `repeat(${bestColumns}, 1fr)`,
            gridTemplateRows: `repeat(${bestRows}, 1fr)`
        };
    };

    // If we have multiple gauge values, render multiple charts
    return (
        <div className={cn("w-full h-full overflow-hidden", props.className)} ref={containerRef}>
            <div
                className="grid gap-1 h-full w-full"
                style={calculateGridLayout()}
            >
                {chartConfigs.map((confg, index) => {
                    return (
                        <div className="w-full h-full min-h-0 min-w-0 overflow-hidden flex items-center justify-center">
                            <div className="w-full h-full max-w-full max-h-full">
                                <Doughnut
                                    {...confg as DoughnutChartConfig} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default GaugeChart;
